import format from "date-fns/format";
import { TFunction } from "i18next";
import { ColDef, ValueGetterParams } from "@ag-grid-community/core";

import { InvoiceItemType, InvoiceStatusCode } from "../../../types";
import SubmittedDateRenderer from "../renderers/SubmittedDateRenderer";
import InvoiceNumberRenderer from "../renderers/InvoiceNumberRenderer";
import TypeRenderer from "../renderers/TypeRenderer";
import PeriodDateRenderer from "../renderers/PeriodDateRenderer";
import SumRenderer from "../renderers/SumRenderer";
import StatusRenderer from "../renderers/StatusRenderer";

export const getFormattedDate = (date?: string) =>
  date ? format(new Date(date), "MMM d, yyyy") : null;

const getTypeFilterOptions = (t: TFunction) => [
  {
    value: "",
    label: t("invoices.any"),
  },
  ...Object.values(InvoiceItemType).map((item) => ({
    value: item,
    label: t(`invoices.type.${item}`),
  })),
];

const getStatusFilterOptions = (t: TFunction) =>
  Object.values(InvoiceStatusCode).map((item) => ({
    value: item,
    label: t(`invoices.statuses.${item}`),
  }));

const getSumFilterOptions = (t: TFunction) => [
  {
    value: "",
    label: t("invoices.any"),
  },
  {
    value: "0,25000",
    label: "0 - 25000",
  },
  {
    value: "25000,124999",
    label: "25000 - 124999",
  },
  {
    value: `125000,249999`,
    label: `125000 - 249999`,
  },
  {
    value: `250000,499999`,
    label: `250000 - 499999`,
  },
  {
    value: `500000`,
    label: `500000+`,
  },
];

export const getCommonColumnDefs = (
  t: TFunction
): ColDef[] => [
  {
    headerName: t("invoices.grid.cols.submittedDate"),
    field: "submittedDate",
    width: 200,
    minWidth: 200,
    cellRenderer: "submittedDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      placeholder: t("invoices.grid.placeholder.date"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("invoices.grid.cols.invoiceNumber"),
    field: "invoiceNumber",
    cellRenderer: "invoiceNumberRenderer",
    width: 170,
    minWidth: 170,
    flex: 1,
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("invoices.grid.cols.transactionType"),
    field: "invoiceTarget",
    width: 156,
    minWidth: 156,
    cellRenderer: "typeRenderer",
    filter: "singleSelectFilter",
    floatingFilterComponent: "singleSelectComponent",
    floatingFilterComponentParams: {
      id: "type-single-select-filter",
      width: 200,
      suppressFilterButton: true,
      options: getTypeFilterOptions(t),
    },
  },
  {
    headerName: t("invoices.grid.cols.period"),
    colId: "invoicePeriod",
    valueGetter: (params: ValueGetterParams) => ({
      periodStartDate: params.data?.periodStartDate,
      periodEndDate: params.data?.periodEndDate,
    }),
    width: 200,
    minWidth: 200,
    flex: 1,
    sortable: false,
    cellRenderer: "periodDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      placeholder: t("invoices.grid.placeholder.date"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("invoices.grid.cols.sum", { currency: "$" }),
    field: "sum",
    minWidth: 180,
    width: 180,
    cellRenderer: "sumRenderer",
    filter: "singleSelectFilter",
    floatingFilterComponent: "singleSelectComponent",
    floatingFilterComponentParams: {
      id: "sum-single-select-filter",
      suppressFilterButton: true,
      options: getSumFilterOptions(t),
    },
  },
  {
    headerName: t("invoices.grid.cols.statusCode"),
    field: "statusCode",
    width: 160,
    minWidth: 160,
    cellRenderer: "statusRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "status-multi-select-filter",
      width: 200,
      suppressFilterButton: true,
      options: getStatusFilterOptions(t),
    },
  },
  {
    headerName: "",
    sortable: false,
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "invoices-clear-filters-button",
    },
    width: 80,
    minWidth: 80,
  },
];

export const getCommonRenderers = () => ({
  submittedDateRenderer: SubmittedDateRenderer,
  invoiceNumberRenderer: InvoiceNumberRenderer,
  typeRenderer: TypeRenderer,
  periodDateRenderer: PeriodDateRenderer,
  sumRenderer: SumRenderer,
  statusRenderer: StatusRenderer,
});
